import { useState } from 'react';
import type { ReactNode } from 'react';
import { ProviderForm, providerFormStateFrom, toError } from '@/modules/ai/shared';
import type { ProviderFormResult } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import type { ProviderSummary } from '@/api/schemas/ai';

/**
 * Editing a platform provider.
 *
 * The credential field is present but OPTIONAL here: leaving it empty sends
 * no credential, and the stored key stays exactly as it is. Filling it in
 * replaces the key — which is what an operator who types into a password box
 * on a provider they are editing means.
 */
export type ProviderEditFormProps = {
  provider: ProviderSummary;
  onSaved: () => void;
};

export function ProviderEditForm({ provider, onSaved }: ProviderEditFormProps): ReactNode {
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const handleSubmit = async (result: ProviderFormResult): Promise<void> => {
    setIsSaving(true);
    setSubmitError(null);
    try {
      await aiApi.updatePlatformProvider(provider.id, result.provider, provider.version);

      if (result.apiKey !== null) {
        await aiApi.rotatePlatformCredential(provider.id, { apiKey: result.apiKey });
      }

      onSaved();
    } catch (cause) {
      setSubmitError(toError(cause));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ProviderForm
      initialValues={providerFormStateFrom(provider)}
      submitLabel="Save provider"
      isSubmitting={isSaving}
      submitError={submitError}
      credentialLabel="Replace credential"
      credentialHint="Leave empty to keep the current key. A new key replaces the stored one and is never shown again."
      onSubmit={(result) => {
        void handleSubmit(result);
      }}
    />
  );
}
